import { create } from 'zustand';
import type { Program } from '@vta/types';
import type { RootSearch } from '../lib/url-state.ts';

/**
 * Filter store — bridges router search params.
 *
 * IMPORTANT: this store is a *mirror*, not the source of truth. The router
 * search params are canonical. Components that mutate state should call the
 * router's `navigate({ search: ... })` and let the root route call
 * `useFilterStore.getState().hydrate(search)` to keep the mirror in sync.
 *
 * This split exists because some non-route components (e.g. the globe scene
 * deep inside layout) need to read the current filter without depending on
 * the router context, and Zustand selectors give us cheap subscriptions.
 */
export interface FilterState {
  team: string | undefined;
  city: string | undefined;
  country: string | undefined;
  program: Program | undefined;
  division: string | undefined;
  event: string | undefined;
}

export interface FilterStore extends FilterState {
  hydrate: (search: RootSearch) => void;
  reset: () => void;
}

const initialState: FilterState = {
  team: undefined,
  city: undefined,
  country: undefined,
  program: undefined,
  division: undefined,
  event: undefined,
};

export const useFilterStore = create<FilterStore>((set) => ({
  ...initialState,
  hydrate: (search) =>
    set({
      team: search.team,
      city: search.city,
      country: search.country,
      program: search.program,
      division: search.division,
      event: search.event,
    }),
  reset: () => set({ ...initialState }),
}));

/** Stable selector — returns just the filter values (no actions). */
export const selectFilterValues = (s: FilterStore): FilterState => ({
  team: s.team,
  city: s.city,
  country: s.country,
  program: s.program,
  division: s.division,
  event: s.event,
});
